import { useState, useRef, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { EvinAnalysisResult, AnalysisType, SuggestedFix } from '../../shared/types';
import { useAuth } from '../contexts/AuthContext';
import SuggestFixButton from './SuggestFixButton';
import ReportErrorButton from './ReportErrorButton';
import AnalysisFeedback from './AnalysisFeedback';
import VideoComplianceChecklist from './VideoComplianceChecklist';
import { fixMarkdownFormatting, Md, MdBlock } from '../utils/markdown';
import { scoreColor, scoreBg } from '../utils/score';
import { VerdictBadge, RiskBadge } from './ui/badges';
import OriginalInputAccordion from './analysis/OriginalInputAccordion';
import SourceItem from './analysis/SourceItem';
import LegalRef from './analysis/LegalRef';
import ActionBar from './analysis/ActionBar';
import SectionBlock from './analysis/SectionBlock';
import PastelScoreRing from './ui/PastelRing';

interface Props {
  result: EvinAnalysisResult;
  onNewAnalysis: () => void;
  onSuggestionSaved?: (fix: SuggestedFix) => void;
  analysisLabel?: string;
  analysisType?: AnalysisType;
}

/* ── Helpers ─────────────────────────────────────────────── */

/** Collapse list-item lines into continuous prose (summary should never have bullets). */
function cleanSummary(s: string): string {
  return s
    .replace(/\n\s*[-•]\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/* ── Build full analysis text for global sharing ────────── */

function buildFullAnalysisText(result: EvinAnalysisResult): string {
  const lines: string[] = [];

  if (result.generalAnswer) {
    lines.push(result.generalAnswer);
  } else {
    lines.push(`Score : ${result.score}/100`);
    lines.push(`Defense : ${result.judgeDefenseScore}/100 | Contradicteur : ${result.judgeContradicteurScore}/100`);
    lines.push('');
  }

  if (result.summary) lines.push(result.summary, '');

  if (result.interdictionsFormelles.length > 0) {
    lines.push('## Interdictions formelles', '');
    result.interdictionsFormelles.forEach(it => { lines.push(`**${it.element}**`, it.explanation, `Ref: ${it.legalBasis}`, ''); });
  }
  if (result.pointsDeVigilance.length > 0) {
    lines.push('## Points de vigilance', '');
    result.pointsDeVigilance.forEach(it => {
      lines.push(`**${it.element}** (${it.riskLevel})`, it.explanation);
      if (it.recommendation) lines.push(`Recommandation : ${it.recommendation}`);
      lines.push(`Ref: ${it.legalBasis}`, '');
    });
  }
  if (result.conformePoints.length > 0) {
    lines.push('## Points conformes', '');
    result.conformePoints.forEach(it => { lines.push(`**${it.element}**`, it.explanation, `Ref: ${it.legalBasis}`, ''); });
  }
  if (result.arbitrageDetails) lines.push('## Arbitrage', '', result.arbitrageDetails);

  return lines.join('\n');
}

/* ── Main component ─────────────────────────────────────── */

export default function ResultsDisplay({ result, onNewAnalysis, onSuggestionSaved, analysisLabel, analysisType }: Props) {
  const { features } = useAuth();
  const isFree = !features.copyText; // Free plan: no copy, no select, no share
  const isGeneralQuestion = !!result.generalAnswer;
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (result.score / 100) * circumference;

  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(result.generalAnswer ?? '');
  const [displayText, setDisplayText] = useState(result.generalAnswer ?? '');
  const [hovered, setHovered] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync local text state when the result prop changes — otherwise clicking
  // a different history item keeps the first analysis' answer in the editor
  // (useState's initial value is only applied on mount).
  useEffect(() => {
    setDisplayText(result.generalAnswer ?? '');
    setEditedText(result.generalAnswer ?? '');
    setIsEditing(false);
  }, [result.generalAnswer, result.traceId]);

  // Source scroll/highlight state
  const [highlightedSourceIdx, setHighlightedSourceIdx] = useState<number | null>(null);
  const sourceRefs = useRef<(HTMLLIElement | null)[]>([]);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleRefClick = useCallback((articleRef: string) => {
    // Find the best matching source
    const normalized = articleRef.toUpperCase().replace(/\s+/g, '');
    const idx = result.sources.findIndex(s => {
      const ref = (s.reference + ' ' + s.title).toUpperCase().replace(/\s+/g, '');
      return ref.includes(normalized);
    });

    if (idx === -1) return;

    // Highlight + expand
    setHighlightedSourceIdx(idx);

    // Scroll to source
    setTimeout(() => {
      sourceRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);

    // Clear highlight after 3s
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedSourceIdx(null), 3000);
  }, [result.sources]);

  const handleStartEdit = useCallback(() => {
    setEditedText(displayText);
    setIsEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, [displayText]);

  const handleValidate = useCallback(() => {
    setDisplayText(editedText);
    setIsEditing(false);
  }, [editedText]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const fullText = buildFullAnalysisText(
    isGeneralQuestion ? { ...result, generalAnswer: displayText } : result,
  );

  const defenseWins = result.judgeDefenseScore >= result.judgeContradicteurScore;

  return (
    <div
      className="animate-fade-in max-w-[760px] mx-auto"
      style={isFree ? { userSelect: 'none', WebkitUserSelect: 'none' } : undefined}
      onCopy={isFree ? (e) => e.preventDefault() : undefined}
    >
      {/* Free tier upgrade banner */}
      {isFree && (
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 shader:from-violet-500/10 shader:to-indigo-500/10 border border-violet-200 dark:border-violet-800 shader:border-violet-400/20">
          <p className="text-sm text-violet-800 dark:text-violet-300 shader:text-violet-200 font-medium mb-1">
            Analyse en lecture seule
          </p>
          <p className="text-xs text-violet-600 dark:text-violet-400 shader:text-violet-200">
            Passez au plan Pro pour copier, partager et conserver vos analyses dans l'historique.
          </p>
        </div>
      )}

      {/* Back to home */}
      <button
        onClick={onNewAnalysis}
        className="flex items-center gap-1.5 mb-6 text-sm text-slate-500 dark:text-slate-400 shader:text-white/80 pastel:text-(--p-ink)/60 hover:text-slate-800 dark:hover:text-white shader:hover:text-white pastel:hover:text-(--p-ink) transition-colors"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        <span>Nouvelle analyse</span>
      </button>

      {/* ── General Question ──────────────────────────────── */}
      {isGeneralQuestion ? (
        <div className="mb-8">
          {/* Votre question — en haut, comme les autres features */}
          {(result.originalInput || result.originalImages?.length || result.originalLabelUsage || result.originalEvent) && (
            <div className="mb-6">
              <OriginalInputAccordion title={analysisLabel ? `Votre ${analysisLabel.toLowerCase()}` : 'Votre question'} input={result.originalInput} context={result.originalContext} images={result.originalImages} labelUsage={result.originalLabelUsage} event={result.originalEvent} />
            </div>
          )}
          <div className="flex items-center gap-2.5 mb-4">
            <svg className="w-6 h-6 text-slate-400 dark:text-white/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <path d="M8 9h8" /><path d="M8 13h5" />
            </svg>
            <h2 className="font-display text-xl text-slate-900 dark:text-white">R&eacute;ponse</h2>
          </div>

          <div
            className="relative group/result"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <div className={`flex justify-center transition-opacity duration-200 ${hovered && !isEditing ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <ActionBar text={displayText} onEdit={handleStartEdit} result={{ ...result, generalAnswer: displayText }} />
            </div>

            <div className="rounded-xl pastel:rounded-3xl border border-violet-200 dark:border-violet-800/50 shader:border-violet-400/20 pastel:border-(--tint-strong)/30 bg-violet-50/50 dark:bg-violet-900/10 shader:bg-violet-900/10 pastel:bg-(--tint-soft) shader:backdrop-blur-xl px-8 py-8 md:px-10 md:py-10">
              {isEditing ? (
                <div>
                  <div className="glow-input">
                    <textarea
                      ref={textareaRef}
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      className="w-full min-h-[200px] rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 shader:backdrop-blur-sm px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-shadow resize-y"
                    />
                  </div>
                  <div className="mt-3 flex gap-2 justify-end">
                    <button onClick={handleCancelEdit} className="font-display px-4 py-2 rounded-lg text-sm border border-slate-300 dark:border-slate-600 shader:border-white/15 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors">Annuler</button>
                    <button onClick={handleValidate} className="font-display px-4 py-2 rounded-lg text-sm bg-white text-slate-900 dark:bg-white dark:text-slate-900 hover:bg-white/90 transition-colors">Valider</button>
                  </div>
                </div>
              ) : (
                <div className="prose-evin text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{fixMarkdownFormatting(displayText)}</ReactMarkdown>
                </div>
              )}
            </div>

            <div className={`flex justify-center transition-opacity duration-200 ${hovered && !isEditing ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <ActionBar text={displayText} onEdit={handleStartEdit} result={{ ...result, generalAnswer: displayText }} />
            </div>
          </div>

          {result.summary && (
            <div className="mt-6 italic">
              <MdBlock className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/80">{cleanSummary(result.summary)}</MdBlock>
            </div>
          )}
        </div>

      /* ── Scored Analysis ────────────────────────────────── */
      ) : (
        <div className="mb-10">
          <div className="text-center">
            <div className="inline-flex flex-col items-center">
              {/* Mode pastel : l'anneau segmenté aux quatre teintes du nuancier
                  remplace le cercle monochrome (rendu ci-dessous, masqué). */}
              <div className="hidden pastel:block mb-4">
                <PastelScoreRing score={result.score} size={150} />
              </div>
              <svg width="140" height="140" className="mb-4 pastel:hidden">
                <circle cx="70" cy="70" r="45" fill="none" strokeWidth="8" className="stroke-slate-200 dark:stroke-slate-700" />
                <circle cx="70" cy="70" r="45" fill="none" strokeWidth="8" strokeLinecap="round" className={`score-circle ${scoreBg(result.score)}`}
                  style={{ strokeDasharray: circumference, strokeDashoffset: offset, transform: 'rotate(-90deg)', transformOrigin: 'center' }} />
                <text x="70" y="65" textAnchor="middle" className={`text-3xl font-bold fill-current ${scoreColor(result.score)}`}>{result.score}</text>
                <text x="70" y="82" textAnchor="middle" className="text-xs fill-slate-400">/100</text>
              </svg>
              <VerdictBadge verdict={result.verdict} />
            </div>

            {result.summary && (
              <div className="mt-6 max-w-[600px] mx-auto">
                <MdBlock className="text-sm text-slate-600 dark:text-slate-400 shader:text-white/90">{cleanSummary(result.summary)}</MdBlock>
              </div>
            )}

            {/* Pipeline 3 juges — Défense + Contradicteur → Arbitre (verdict final) */}
            <div className="mt-8">
              <div className="flex items-start justify-center gap-2 sm:gap-4 text-sm">
                {/* Juge Défense */}
                <div className="group/def flex flex-col items-center relative max-w-[120px]">
                  <span className={`text-lg font-semibold ${defenseWins ? 'text-emerald-500' : 'text-slate-400 dark:text-slate-500 shader:text-white'} ${defenseWins ? '' : 'opacity-60'}`}>
                    {result.judgeDefenseScore}<span className="text-xs font-normal text-slate-400 shader:text-white/60">/100</span>
                  </span>
                  <span className={`text-xs text-slate-500 dark:text-slate-400 shader:text-white mt-0.5 cursor-help text-center ${defenseWins ? '' : 'opacity-60'}`}>Juge D&eacute;fense</span>
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 px-3 py-2 rounded-lg bg-slate-900 dark:bg-white shader:bg-white text-white dark:text-slate-900 shader:text-slate-900 text-[11px] leading-relaxed shadow-lg opacity-0 pointer-events-none group-hover/def:opacity-100 transition-opacity duration-200 z-20">
                    L'avocat de la d&eacute;fense plaide la conformit&eacute;&nbsp;: il cherche tout ce qui est autoris&eacute; par la Loi &Eacute;vin dans votre contenu.
                  </div>
                </div>

                <span className="text-slate-300 dark:text-slate-600 shader:text-white/60 text-base leading-[1.7] select-none">+</span>

                {/* Juge Contradicteur */}
                <div className="group/con flex flex-col items-center relative max-w-[120px]">
                  <span className={`text-lg font-semibold ${!defenseWins ? 'text-red-500' : 'text-slate-400 dark:text-slate-500 shader:text-white'} ${!defenseWins ? '' : 'opacity-60'}`}>
                    {result.judgeContradicteurScore}<span className="text-xs font-normal text-slate-400 shader:text-white/60">/100</span>
                  </span>
                  <span className={`text-xs text-slate-500 dark:text-slate-400 shader:text-white mt-0.5 cursor-help text-center ${!defenseWins ? '' : 'opacity-60'}`}>Juge Contradicteur</span>
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-56 px-3 py-2 rounded-lg bg-slate-900 dark:bg-white shader:bg-white text-white dark:text-slate-900 shader:text-slate-900 text-[11px] leading-relaxed shadow-lg opacity-0 pointer-events-none group-hover/con:opacity-100 transition-opacity duration-200 z-20">
                    Le contradicteur plaide &agrave; charge&nbsp;: il identifie tout ce qui pourrait poser un probl&egrave;me juridique ou &ecirc;tre interpr&eacute;t&eacute; comme non conforme.
                  </div>
                </div>

                <span className="text-slate-300 dark:text-slate-600 shader:text-white/60 text-base leading-[1.7] select-none">&rarr;</span>

                {/* Arbitre — verdict final */}
                <div className="group/arb flex flex-col items-center relative max-w-[130px]">
                  <span className={`text-lg font-bold ${scoreColor(result.score)}`}>
                    {result.score}<span className="text-xs font-normal text-slate-400 shader:text-white/60">/100</span>
                  </span>
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 shader:text-white mt-0.5 cursor-help text-center inline-flex items-center gap-1">
                    Arbitre
                    <svg className="w-3 h-3 text-slate-400 dark:text-slate-500 shader:text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                  </span>
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 px-3 py-2 rounded-lg bg-slate-900 dark:bg-white shader:bg-white text-white dark:text-slate-900 shader:text-slate-900 text-[11px] leading-relaxed shadow-lg opacity-0 pointer-events-none group-hover/arb:opacity-100 transition-opacity duration-200 z-20">
                    L'arbitre p&egrave;se les deux plaidoiries et rend le <strong>score final</strong> &mdash; ce n'est pas une moyenne des deux juges. Il &eacute;carte les arguments non &eacute;tay&eacute;s par la loi ou la jurisprudence.
                  </div>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-400 dark:text-slate-500 shader:text-white/80 text-center max-w-[440px] mx-auto">
                Les deux juges plaident, l'arbitre tranche. Le grand score ci-dessus est le verdict de l'arbitre, pas une moyenne.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Original Input ──────────────────────────────────── */}
      {!isGeneralQuestion && (result.originalInput || result.originalImages?.length || result.originalLabelUsage || result.originalEvent) && (
        <div className="mb-8">
          <OriginalInputAccordion title={analysisLabel ? `Votre ${analysisLabel.toLowerCase()}` : 'Votre demande'} input={result.originalInput} context={result.originalContext} images={result.originalImages} labelUsage={result.originalLabelUsage} event={result.originalEvent} />
        </div>
      )}

      {/* Video checklist */}
      {result.socialMeta?.format === 'video' && (
        <div className="mb-8">
          <VideoComplianceChecklist />
        </div>
      )}

      {/* ── Hors périmètre Loi Évin (religion / politique / conflit) ──
           Avertissement annexe, n'affecte pas le score. N'apparaît que si
           l'arbitre a détecté une référence explicite. */}
      {!isGeneralQuestion && result.outOfScopeFlags && result.outOfScopeFlags.length > 0 && (
        <div className="mt-8 mb-8 rounded-xl border border-amber-300 dark:border-amber-700/50 shader:border-amber-400/30 bg-amber-50 dark:bg-amber-900/15 shader:bg-amber-500/10 shader:backdrop-blur-xl px-6 py-5">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-amber-600 dark:text-amber-400 shader:text-amber-300 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg>
            <h3 className="font-display text-sm text-amber-700 dark:text-amber-300 shader:text-amber-200 uppercase tracking-wide">Hors périmètre Loi Évin</h3>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/60 mb-4">
            Ces points ne relèvent pas de la Loi Évin et <strong>n'affectent pas le score</strong>. Ils signalent un risque juridique distinct (dépôt de marque, image) — à faire valider par un conseil en propriété industrielle.
          </p>
          <ul className="space-y-4">
            {result.outOfScopeFlags.map((flag, i) => (
              <li key={i} className="text-sm">
                <p className="font-semibold text-slate-900 dark:text-white shader:text-white mb-1">
                  {flag.element}
                  <span className="ml-2 align-middle inline-block px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 shader:bg-amber-500/20 shader:text-amber-200">{flag.topic}</span>
                </p>
                <p className="text-slate-600 dark:text-slate-300 shader:text-white/80 leading-relaxed">{flag.explanation}</p>
                <p className="text-xs text-amber-700 dark:text-amber-400 shader:text-amber-300 mt-1">Réf.&nbsp;: {flag.legalBasis}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Sections ──────────────────────────────────────── */}
      <div className="space-y-8 stagger-children">
        {/* Interdictions */}
        {!isGeneralQuestion && result.interdictionsFormelles.length > 0 && (
          <SectionBlock
            className="rounded-xl border border-red-200 dark:border-red-800/50 shader:border-red-500/20 bg-red-50/50 dark:bg-red-900/10 shader:bg-red-900/10 shader:backdrop-blur-xl px-6 py-6"
            copyText={'## Interdictions formelles\n\n' + result.interdictionsFormelles.map(it => `**${it.element}**\n${it.explanation}\nRef: ${it.legalBasis}`).join('\n\n')}
          >
            <h3 className="font-display text-sm text-red-700 dark:text-red-400 uppercase tracking-wide mb-5">Interdictions formelles</h3>
            <ul className="space-y-6">
              {result.interdictionsFormelles.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  <p className="font-medium text-slate-900 dark:text-white mb-1"><Md>{item.element}</Md></p>
                  <p className="text-slate-600 dark:text-slate-400 shader:text-white/90 leading-relaxed"><Md>{item.explanation}</Md></p>
                  <LegalRef text={item.legalBasis} colorClass="text-red-600 dark:text-red-400 shader:text-red-300" onRefClick={handleRefClick} />
                </li>
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* Points de vigilance */}
        {!isGeneralQuestion && result.pointsDeVigilance.length > 0 && (
          <SectionBlock
            className="rounded-xl border border-amber-200 dark:border-amber-800/50 shader:border-amber-400/20 bg-amber-50/50 dark:bg-amber-900/10 shader:bg-amber-900/10 shader:backdrop-blur-xl px-6 py-6"
            copyText={'## Points de vigilance\n\n' + result.pointsDeVigilance.map(it => `**${it.element}** (${it.riskLevel})\n${it.explanation}${it.recommendation ? '\nRecommandation : ' + it.recommendation : ''}\nRef: ${it.legalBasis}`).join('\n\n')}
          >
            <h3 className="font-display text-sm text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-5">Points de vigilance</h3>
            <ul className="space-y-6">
              {result.pointsDeVigilance.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-slate-900 dark:text-white"><Md>{item.element}</Md></span>
                    <RiskBadge level={item.riskLevel} />
                  </div>
                  <p className="text-slate-600 dark:text-slate-400 shader:text-white/90 leading-relaxed"><Md>{item.explanation}</Md></p>
                  {item.recommendation && (
                    <div className="mt-2 text-violet-600 dark:text-violet-400 shader:text-violet-200 text-xs leading-relaxed">
                      <span className="font-semibold">Recommandation :</span>
                      <div className="mt-1"><Md inheritBold>{item.recommendation}</Md></div>
                    </div>
                  )}
                  <LegalRef text={item.legalBasis} colorClass="text-slate-500 dark:text-slate-400 shader:text-white/70" onRefClick={handleRefClick} />
                </li>
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* Points conformes */}
        {!isGeneralQuestion && result.conformePoints.length > 0 && (
          <SectionBlock
            className="rounded-xl border border-emerald-200 dark:border-emerald-800/50 shader:border-emerald-400/20 bg-emerald-50/50 dark:bg-emerald-900/10 shader:bg-emerald-900/10 shader:backdrop-blur-xl px-6 py-6"
            copyText={'## Points conformes\n\n' + result.conformePoints.map(it => `**${it.element}**\n${it.explanation}\nRef: ${it.legalBasis}`).join('\n\n')}
          >
            <h3 className="font-display text-sm text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-5">Points conformes</h3>
            <ul className="space-y-6">
              {result.conformePoints.map((item, i) => (
                <li key={i} className="text-sm leading-relaxed">
                  <p className="font-medium text-slate-900 dark:text-white mb-1"><Md>{item.element}</Md></p>
                  <p className="text-slate-600 dark:text-slate-400 shader:text-white/90 leading-relaxed"><Md>{item.explanation}</Md></p>
                  <LegalRef text={item.legalBasis} colorClass="text-emerald-600 dark:text-emerald-400 shader:text-emerald-300" onRefClick={handleRefClick} />
                </li>
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* Sources */}
        {result.sources.length > 0 && (
          <SectionBlock
            className="rounded-xl border border-slate-200 dark:border-slate-700 shader:border-white/10 bg-white dark:bg-slate-900 shader:bg-white/5 shader:backdrop-blur-xl px-6 py-6"
            copyText={'## Sources juridiques\n\n' + result.sources.map(s => `[${s.type}] ${s.title} — ${s.reference}`).join('\n')}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-sm text-slate-700 dark:text-slate-300 shader:text-white uppercase tracking-wide">Sources juridiques</h3>
              {result.sources.some(s => s.articleContent) && (
                <span className="text-[10px] text-slate-400 dark:text-slate-500 shader:text-white/70">Source : DILA / L&eacute;gifrance</span>
              )}
            </div>
            <ul className="space-y-2">
              {result.sources.map((source, i) => (
                <SourceItem
                  key={i}
                  source={source}
                  forceExpanded={highlightedSourceIdx === i}
                  sourceRef={(el: HTMLLIElement | null) => { sourceRefs.current[i] = el; }}
                />
              ))}
            </ul>
          </SectionBlock>
        )}

        {/* Arbitrage Details — rendered as markdown */}
        {!isGeneralQuestion && result.arbitrageDetails && (
          <SectionBlock
            className="rounded-xl border border-slate-200 dark:border-slate-700 shader:border-white/10 bg-white dark:bg-slate-900 shader:bg-white/5 shader:backdrop-blur-xl px-6 py-6"
            copyText={'## Détails de l\'arbitrage\n\n' + result.arbitrageDetails}
          >
            <details>
              <summary className="font-display text-sm text-slate-700 dark:text-slate-300 shader:text-white cursor-pointer mb-4">D&eacute;tails de l'arbitrage</summary>
              <MdBlock className="text-sm text-slate-600 dark:text-slate-400 shader:text-white/70">{result.arbitrageDetails}</MdBlock>
            </details>
          </SectionBlock>
        )}
      </div>

      {/* ── Rappel mention sanitaire pour les analyses d'idées ──
           Nom de marque / slogan / concept ne sont pas des supports
           publicitaires : l'absence du message sanitaire n'est pas un
           manquement ici, mais le deviendra au moment de la diffusion. */}
      {!isGeneralQuestion &&
        analysisType &&
        (analysisType === 'brand_name' ||
          analysisType === 'slogan' ||
          analysisType === 'concept') && (
          <div className="mt-6 px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 shader:bg-white/5 shader:backdrop-blur-xl border border-slate-200 dark:border-slate-700/50 shader:border-white/10 text-xs text-slate-600 dark:text-slate-400 shader:text-white/90 leading-relaxed">
            <span className="font-medium text-slate-700 dark:text-slate-300 shader:text-white">Rappel&nbsp;:</span>{' '}
            la mention sanitaire «&nbsp;L'abus d'alcool est dangereux pour la santé&nbsp;»
            deviendra obligatoire dès que ce {analysisType === 'brand_name' ? 'nom' : analysisType === 'slogan' ? 'slogan' : 'concept'} sera porté sur un support publicitaire (étiquette, affiche, campagne, réseaux sociaux). Elle n'a pas à figurer à ce stade.
          </div>
        )}

      {/* ── Fix auto suggestion (for non-conform results) ──── */}
      {!isGeneralQuestion && analysisType && (
        <SuggestFixButton result={result} analysisType={analysisType} onSaved={onSuggestionSaved} />
      )}

      {/* ── Langfuse feedback (thumbs up/down) ──────────────── */}
      {result.traceId && <AnalysisFeedback traceId={result.traceId} />}

      {/* ── Signalement qualité (canal RGPD art. 22) — uniquement pour les analyses scorées ── */}
      {!isGeneralQuestion && (
        <ReportErrorButton result={result} analysisType={analysisType} />
      )}

      {/* ── Global share bar ─────────────────────────────────
           Toujours rendue : ActionBar grise (et verrouille) les actions que le
           plan n'inclut pas, au lieu de les escamoter. Un visiteur arrivé par
           code promo voit ainsi ce que débloque l'abonnement. */}
      <div className="mt-8 mb-2 flex justify-center">
        <ActionBar showLocked text={fullText} result={isGeneralQuestion ? { ...result, generalAnswer: displayText } : result} />
      </div>

      {/* Actions */}
      <div className="flex justify-center">
        <button onClick={onNewAnalysis} className="font-display px-8 py-3 rounded-xl pastel:rounded-full text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 shader:from-white shader:to-white shader:text-slate-900 shader:hover:from-white/90 shader:hover:to-white/90 pastel:bg-none pastel:bg-violet-500 pastel:hover:bg-violet-600 pastel:hover:scale-[1.02] transition-all">
          Nouvelle analyse
        </button>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 space-y-2 text-center text-xs text-slate-400 dark:text-slate-500 shader:text-white/70">
        <p>
          Cette analyse ne constitue pas un avis juridique. Les textes de loi sont fournis
          {' '}&agrave; titre informatif et ne sont pas opposables &mdash; seuls les PDF sign&eacute;s
          publi&eacute;s au Journal officiel de la R&eacute;publique fran&ccedil;aise font foi.
          Consultez un avocat sp&eacute;cialis&eacute; pour une validation officielle.
        </p>
        <p>
          Donn&eacute;es juridiques : DILA / L&eacute;gifrance &mdash;{' '}
          <a href="https://www.etalab.gouv.fr/wp-content/uploads/2017/04/ETALAB-Licence-Ouverte-v2.0.pdf" target="_blank" rel="noopener noreferrer"
            className="underline hover:text-slate-600 dark:hover:text-slate-300 shader:hover:text-white/70">
            Licence Ouverte 2.0
          </a>
        </p>
      </div>
    </div>
  );
}
